import { SearchIcon, CloseIcon } from 'assets/images';
import { AppActionInput } from 'components';
import useKeyboardNavigation from '@/shared/hooks/useKeyboardNavigation';
import useBehavioralKeyboard from '@/shared/hooks/useBehavioralKeyboard';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './search.scss';

const searchCache = {};
export const clearSearchCache = scopeKey => {
   if (scopeKey) {
      delete searchCache[scopeKey];
   } else {
      Object.keys(searchCache).forEach(key => delete searchCache[key]);
   }
};

export default function SearchBox({
   scopeKey = '',
   placeholder = 'Search',
   onChange = () => {},
   autoFocus = false,
   showEmoji = false,
}) {
   const [value, setValue] = useState(() => searchCache[scopeKey] || '');
   const inputRef = useRef(null);

   // When scopeKey changes, update value from cache
   useEffect(() => {
      const cachedVal = searchCache[scopeKey] || '';
      if (value !== cachedVal) {
         setValue(cachedVal);
      }
      // On mount or scopeKey change, ensure parent is synced with current (potentially cached) value
      onChange(cachedVal.trim());
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [scopeKey]);

   const handleClear = useCallback(() => {
      setValue('');
      searchCache[scopeKey] = '';
      onChange('');
      if (inputRef.current) {
         inputRef.current.focus();
      }
   }, [scopeKey, onChange]);

   // Keyboard shortcuts
   const { handleKeyDown } = useBehavioralKeyboard(inputRef, {
      onEscape: () => {
         if (value) {
            handleClear();
            return true;
         }
         return false; // Let it bubble if search is already empty
      },
   });

   const keyboardConfig = useMemo(
      () => ({
         'CmdOrCtrl+k': e => {
            e.preventDefault();
            inputRef.current?.focus();
         },
      }),
      [],
   );

   useKeyboardNavigation(keyboardConfig, {
      enabled: true,
      preventDefault: false,
      allowWhileTyping: true,
   });

   // Update input and cache on change
   const handleChange = useCallback(
      val => {
         setValue(val);
         if (scopeKey) {
            searchCache[scopeKey] = val;
         }
         onChange(val.trim());
      },
      [scopeKey, onChange],
   );

   return (
      <AppActionInput
         ref={inputRef}
         containerClassName="search-box"
         prefixIcon={<SearchIcon className="search-box-icon" />}
         type="text"
         name="search-input"
         autoComplete="off"
         placeholder={placeholder}
         value={value}
         onChange={handleChange}
         onKeyDown={handleKeyDown}
         autoFocus={autoFocus}
         maxLength={100}
         showCharCount={false}
         showEmoji={showEmoji}
         trailingComponent={
            value ? (
               <button type="button" className="search-box-close ct-flexCenter" title="Clear" onClick={handleClear}>
                  <CloseIcon className="search-box-close-icon" />
               </button>
            ) : null
         }
      />
   );
}

SearchBox.propTypes = {
   scopeKey: PropTypes.string,
   placeholder: PropTypes.string,
   onChange: PropTypes.func,
   autoFocus: PropTypes.bool,
   showEmoji: PropTypes.bool,
};
